'use client';

import {
  Box,
  ButtonGroup,
  Center,
  Checkbox,
  Flex,
  Heading,
  IconButton,
  Pagination,
  Stack,
  Table,
} from '@chakra-ui/react';
import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { EmptyState } from '../ui';

export type SelectionTableHandle = {
  resetSelectedRows?: () => void;
};

type BaseSelectionTableProps<T> = {
  title?: string;
  items: T[];
  columns: {
    [K in keyof T]?: {
      label: string;
      width?: string;
      render?: (item: T) => React.ReactNode;
      align?: 'start' | 'center' | 'end';
    };
  }[];
  height?: string;
  hideColumns?: (keyof T)[];
  rowKey?: keyof T; //*REQUIRED:if the key 'id' is not available inside the items:T[], specify the key here for distinguishing each item
  stickyColumns?: boolean;
  isPaginated?: boolean;
  pageSize?: number;
  resetSelectedRows?: (resetFn: () => void) => void;
  noItemsEmptyStateMessage?: {
    title?: string;
    description?: string;
  };
  //render row higlgiht adn sx
  getRowStyle?: (item: T) =>
  | {
      bg?: string;
      _hover?: { bg?: string };
    }
  | undefined;
};

// Do not allow if enableSelect is false
type SelectionDisabledProps = {
  enableSelect?: false | undefined;
  multiSelect?: never;
  onRowsSelect?: never;
  selectedRows?: never;
};

// Allow if enableSelect is true
type SelectionEnabledProps<T> = {
  enableSelect?: true;
  multiSelect?: boolean;
  onRowsSelect?: (item: T) => void;
  selectedRows?: (items: T[]) => void;
};

type SelectionTableProps<T> = BaseSelectionTableProps<T> &
  (SelectionDisabledProps | SelectionEnabledProps<T>);

function SelectionTableInner<T>(
  props: SelectionTableProps<T>,
  ref: React.Ref<SelectionTableHandle>,
) {
  const {
    title,
    items,
    columns,
    hideColumns,
    height,
    enableSelect = false,
    rowKey = 'id' as keyof T,
    stickyColumns = false,
    isPaginated = false,
    pageSize = 10,
    noItemsEmptyStateMessage,
    getRowStyle,

  } = props;

  const multiSelect = enableSelect && props?.multiSelect;
  const onRowsSelect = enableSelect ? props?.onRowsSelect : undefined;
  const selectedRows = enableSelect ? props?.selectedRows : undefined;

  const [page, setPage] = useState<number>(1);
  const [selectedTableRows, setSelectedTableRows] = useState<T[]>([]);

  const [sortConfig, setSortConfig] = useState<{
    key: keyof T;
    direction: 'asc' | 'desc';
  } | null>(null);
  const indeterminate =
    selectedTableRows.length > 0 && selectedTableRows.length < items.length;

  // reset selected rows
  useImperativeHandle(ref, () => ({
    resetSelectedRows: () => setSelectedTableRows([]),
  }));

  const handleRowSelect = (item: T) => {
    if (!enableSelect) return;

    if (!multiSelect) {
      // single select mode
      setSelectedTableRows([item]); //selected rows
      selectedRows?.([item]);
    } else {
      // multi-select mode: toggle selection
      const exists = selectedTableRows.some((s) => s[rowKey] === item[rowKey]);
      const newSelection = exists
        ? selectedTableRows.filter((s) => s[rowKey] !== item[rowKey])
        : [...selectedTableRows, item];

      setSelectedTableRows((prev) => {
        const newSelection = exists
          ? prev.filter((s) => s[rowKey] !== item[rowKey])
          : [...prev, item];
        return newSelection;
      });

      selectedRows?.(newSelection);
    }
  };

  const handleSort = (key: keyof T) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        // toggle direction
        return {
          key,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key, direction: 'asc' };
    });
  };
  const filteredColumns = useMemo(
    () =>
      columns.filter(
        (col) => !hideColumns?.includes(Object.keys(col)[0] as keyof T),
      ),
    [columns, hideColumns],
  );

  const sortedItems = useMemo(() => {
    const data = isPaginated
      ? items.slice((page - 1) * pageSize, page * pageSize)
      : items;
    if (!sortConfig) return data;

    const { key, direction } = sortConfig;
    return [...data].sort((a, b) => {
      const aValue = a[key];
      const bValue = b[key];

      // Number
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Date
      if (aValue instanceof Date && bValue instanceof Date) {
        return direction === 'asc'
          ? aValue.getTime() - bValue.getTime()
          : bValue.getTime() - aValue.getTime();
      }

      // Default: string comparison
      return direction === 'asc'
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [items, sortConfig, isPaginated, page, pageSize]);

  if (!items?.length)
    return (
      <Center width="full">
        <EmptyState
          isEmpty
          position="relative"
          title={noItemsEmptyStateMessage?.title || 'No items found'}
          description={
            noItemsEmptyStateMessage?.description ||
            'Please add an item to view the table.'
          }
        />
      </Center>
    );

  return (
    <Stack gap="5" maxHeight={height ? height : 'full'} overflowX="auto">
      <Heading size="xl">{title}</Heading>
      <Table.ScrollArea
        borderWidth="1px"
        rounded="md"
        maxHeight={height ? height : 'full'}
      >
        <Table.Root
          size="sm"
          variant="outline"
          stickyHeader
          css={{
            '& [data-sticky]': {
              position: 'sticky',
              bg: 'bg',
              top: 0,
              _after: {
                content: '""',
                position: 'absolute',
                pointerEvents: 'none',
                top: '0',
                bottom: '-1px',
                width: '32px',
              },
            },
            '& [data-sticky=end]': {
              _after: {
                insetInlineEnd: '0',
                translate: '100% 0',
                shadow: 'inset 8px 0px 8px -8px rgba(0, 0, 0, 0.16)',
              },
            },
            '& [data-sticky=start]': {
              _after: {
                insetInlineStart: '0',
                translate: '-100% 0',
                shadow: 'inset -8px 0px 8px -8px rgba(0, 0, 0, 0.16)',
              },
            },
          }}
        >
          <Table.Header>
            <Table.Row cursor="pointer" bg="bg.subtle">
              {filteredColumns?.map((colObj, index) => {
                const [key, col] = Object.entries(colObj)[0] as [
                  keyof T,
                  (typeof colObj)[keyof T],
                ];
                if (!col) return null;

                return (
                  <Table.ColumnHeader
                    key={`column-header-${String(key)}-${index}`}
                    textAlign={col.align}
                    style={{
                      width: col.width,
                      minWidth: col.width,
                      maxWidth: col.width,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      fontWeight: 'bolder',
                    }}
                    data-sticky={
                      stickyColumns && index === 0 ? 'end' : undefined
                    }
                    left={0}
                    top={0}
                    onClick={() => {
                      handleSort(key);
                    }}
                  >
                    {/* Checkbox for select all */}
                    <Flex gapX={10} alignItems="center">
                      {enableSelect && multiSelect && index === 0 && (
                        <Checkbox.Root
                          cursor="pointer"
                          size="sm"
                          colorPalette="blue"
                          marginRight={2}
                          aria-label="Select all rows"
                          checked={
                            indeterminate
                              ? 'indeterminate'
                              : selectedTableRows.length > 0
                          }
                          onCheckedChange={(changes) => {
                            if (!enableSelect) return;

                            setSelectedTableRows(changes.checked ? items : []);
                            selectedRows?.(changes.checked ? items : []);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox.HiddenInput />
                          <Checkbox.Control>
                            <Checkbox.Indicator />
                          </Checkbox.Control>
                        </Checkbox.Root>
                      )}
                      {col.label}
                      {sortConfig?.key === key && (
                        <Box as="span" ml={1}>
                          {sortConfig.direction === 'asc' ? '↑' : '↓'}
                        </Box>
                      )}
                    </Flex>
                  </Table.ColumnHeader>
                );
              })}
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {sortedItems?.map((item, i) => (
              <Table.Row
                key={`table-row-${String(item?.[rowKey])}-${i}`}
                cursor="pointer"
                bg={
                  enableSelect &&
                  selectedTableRows.some((s) => s[rowKey] === item[rowKey])
                    ? 'gray/50'
                    : (getRowStyle?.(item)?.bg ?? '')
                }
                _hover={{
                  bg:
                    enableSelect &&
                    selectedTableRows.some((s) => s[rowKey] === item[rowKey])
                      ? 'gray/50'
                      : (getRowStyle?.(item)?._hover?.bg ?? undefined),
                }}
              >
                {filteredColumns.map((colObj, idx) => {
                  const [key, col] = Object.entries(colObj)[0] as [
                    keyof T,
                    (typeof colObj)[keyof T],
                  ];
                  if (!col) return null;

                  return stickyColumns && idx === 0 ? (
                    <Table.ColumnHeader
                      key={`filtered-column-header-${String(
                        item?.[rowKey],
                      )}-${i}-${idx}`}
                      textAlign={col.align}
                      data-sticky={'end'}
                      left={0}
                      style={{
                        width: col.width,
                        minWidth: col.width,
                        maxWidth: col.width,
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                      }}
                      onClick={() => {
                        // Only allow selecting the row with checkboxes in multi-select
                        if (!multiSelect) {
                          handleRowSelect(item);
                        }
                      }}
                    >
                      <Flex gapX={10} alignItems="center">
                        {enableSelect && multiSelect && (
                          <Checkbox.Root
                            size="sm"
                            colorPalette="blue"
                            marginRight={2}
                            aria-label="Select row"
                            checked={selectedTableRows.some(
                              (s) => s[rowKey] === item[rowKey],
                            )}
                            onCheckedChange={() => handleRowSelect(item)}
                          >
                            <Checkbox.HiddenInput />
                            <Checkbox.Control>
                              <Checkbox.Indicator />
                            </Checkbox.Control>
                          </Checkbox.Root>
                        )}

                        {col.render ? col.render(item) : String(item[key])}
                      </Flex>
                    </Table.ColumnHeader>
                  ) : (
                    <Table.Cell
                      key={`filtered-column-cell-${String(
                        item?.[rowKey],
                      )}-${i}-${idx}`}
                      textAlign={col.align}
                      style={{
                        width: col.width,
                        minWidth: col.width,
                        maxWidth: col.width,
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                      }}
                    >
                      <Flex gapX={10} alignItems="center">
                        {enableSelect && multiSelect && idx === 0 && (
                          <Checkbox.Root
                            size="sm"
                            colorPalette="blue"
                            marginRight={2}
                            aria-label="Select row"
                            checked={selectedTableRows.some(
                              (s) => s[rowKey] === item[rowKey],
                            )}
                            onCheckedChange={() => handleRowSelect(item)}
                          >
                            <Checkbox.HiddenInput />
                            <Checkbox.Control>
                              <Checkbox.Indicator />
                            </Checkbox.Control>
                          </Checkbox.Root>
                        )}

                        <Flex
                          onClick={() => {
                            if (!enableSelect) return;
                            onRowsSelect?.(item);

                            // Select individual rows by checkboxes only in single-select mode
                            if (!multiSelect) {
                              handleRowSelect(item);
                            }
                          }}
                        >
                          {col.render ? col.render(item) : String(item[key])}
                        </Flex>
                      </Flex>
                    </Table.Cell>
                  );
                })}
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Table.ScrollArea>

      {isPaginated && !!sortedItems?.length && items?.length > pageSize && (
        <Pagination.Root count={items.length} pageSize={pageSize} page={page}>
          <ButtonGroup variant="ghost" size="sm" wrap="wrap">
            <Pagination.PrevTrigger asChild>
              <IconButton onClick={() => setPage((prev) => prev - 1)}>
                <LuChevronLeft />
              </IconButton>
            </Pagination.PrevTrigger>

            <Pagination.Items
              render={(page) => (
                <IconButton
                  variant={{ base: 'ghost', _selected: 'outline' }}
                  onClick={() => setPage(page.value)}
                >
                  {page.value}
                </IconButton>
              )}
            />

            <Pagination.NextTrigger asChild>
              <IconButton onClick={() => setPage((prev) => prev + 1)}>
                <LuChevronRight />
              </IconButton>
            </Pagination.NextTrigger>
          </ButtonGroup>
        </Pagination.Root>
      )}
    </Stack>
  );
}

const SelectionTable = forwardRef(SelectionTableInner) as <T>(
  props: SelectionTableProps<T> & { ref?: React.Ref<SelectionTableHandle> },
) => ReturnType<typeof SelectionTableInner>;

export default SelectionTable;
